import { useGetExecutionFeeUsd } from '@ui/features/Trade/hooks/useGetExecutionFee';
import { useLeverage } from '@ui/features/Trade/store/useLeverage';
import { useTradingPair } from '@ui/features/Trade/store/useTradingPair';
import { PossibleBigNumber } from '@ui/types/utility';
import { getPositionSize } from '@ui/utils/trade/getPositionSize';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

/**
 *
 * to Align with APX,
 *
 * isBeforeFee:
 *
 *  for open position flow,
 *    - display will use before fee and also pass in SC will use before fee as well.
 *    - only when calculation relative, liq. Price / open position fees, will include the fees (isBeforeFee = false)
 *
 */
export const usePositionSize = ({
  inputUsd,
  _leverage,
  isBeforeFee = true,
}: {
  inputUsd: PossibleBigNumber;
  _leverage?: number;
  isBeforeFee?: boolean;
}) => {
  const executionFeeUsd = useGetExecutionFeeUsd();

  const leverage = useLeverage();

  const currentTradePairInfo = useTradingPair((s) => s.useTradingPair());

  const openPositionFeeP = currentTradePairInfo?.feeConfig?.openFeeP;

  return useMemo(() => {
    if (
      isNil(executionFeeUsd) ||
      isNil(currentTradePairInfo) ||
      isNil(openPositionFeeP) ||
      isNil(inputUsd) ||
      inputUsd.isNaN()
    )
      return undefined;

    /**
     * Two cases.
     *
     * 1. Trading pair is standard , the openPositionFeeP will have value
     * 2. Trading pair is Degen, the openPositionFeeP will be 0, which means no fee for open position, and only charge close fee
     */
    return getPositionSize({
      fromUsd: inputUsd,
      executionFeeUsd,
      leverage: _leverage ?? leverage,
      openFeeP: openPositionFeeP,
      isBeforeFee: isBeforeFee, // for open position, positionSize will not include the fees for displaying and calculation
    });
  }, [
    executionFeeUsd,
    currentTradePairInfo,
    openPositionFeeP,
    inputUsd,
    _leverage,
    leverage,
    isBeforeFee,
  ]);
};
